import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import '../styles/AddNormalInvoiceForm.css';
import API_BASE_URL from '../config/apiConfig.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import AddCustomerModal from '../components/modals/AddCustomerModal';
import AddTransporterModal from '../components/modals/AddTransporterModal';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext';
import Swal from 'sweetalert2';

const CreatableSelect = React.forwardRef(({ value, onChange, options, onAddOption, placeholder, creatable = true, onSelect, name, validateOnEnter = false }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionMouseDown = (opt, e) => {
    e.preventDefault();
    onChange(opt);
    setSearchTerm(opt);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(opt), 0);
  };

  const handleBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const handleAddClick = () => {
    onAddOption(searchTerm);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
  };

  const filteredOptions = (Array.isArray(options) ? options : []).filter(opt =>
    opt && opt.toString().toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  const showAddOption = creatable && searchTerm && !filteredOptions.some(opt => opt && opt.toString().toLowerCase() === searchTerm.toLowerCase());

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (isOpen) {
        const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);
        if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
          // Do NOT call e.preventDefault() — let event bubble to form's handleFormKeyDown
          const selectedOpt = filteredOptions[activeIndex];
          onChange(selectedOpt);
          setSearchTerm(selectedOpt);
          setIsOpen(false);
          setActiveIndex(-1);
          if (onSelect) setTimeout(() => onSelect(selectedOpt), 0);
          return;
        } else if (showAddOption && activeIndex === filteredOptions.length) {
          e.preventDefault();
          handleAddClick();
          return;
        }
      } else {
        const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
        if (!hasValue && validateOnEnter) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      // Dropdown open but nothing highlighted — just close and let Enter bubble
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef}>
      <input
        ref={ref}
        name={name}
        className="miracle-input"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder || "Select or Type..."}
        autoComplete="off"
      />
      {isOpen && (filteredOptions.length > 0 || showAddOption) && (
        <div className="custom-select-dropdown" style={{ zIndex: 9999 }}>
          {filteredOptions.map((opt, index) => (
            <div
              key={`${opt}-${index}`}
              className="custom-select-option"
              style={{ backgroundColor: index === activeIndex ? '#e6f7ff' : 'transparent' }}
              onMouseDown={(e) => handleOptionMouseDown(opt, e)}
            >
              {opt}
            </div>
          ))}
          {showAddOption && (
            <div
              className="custom-select-add-option"
              style={{ backgroundColor: filteredOptions.length === activeIndex ? '#e6f7ff' : 'transparent' }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleAddClick();
              }}
            >
              Add "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const ProductCreatableSelect = React.forwardRef(({ value, onChange, products, category, onAddOption, placeholder, onSelect, onPrevField, onTab, allowAdd = true }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (p) => {
    if (p === 'Other') {
      onChange('Other', null);
    } else {
      const calculatedRate = (parseFloat(p.product_sale_price || p.productSalePrice || 0)).toFixed(2);
      onChange(p.productName, { ...p, productSalePrice: calculatedRate, product_sale_price: calculatedRate });
    }
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(p === 'Other' ? 'Other' : p.productName), 0);
  };

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleBlur = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const filteredProducts = (products || []).filter(p => {
    const matchesCategory = !category || p.categoryName === category;
    const matchesSearch = (p.productName || '').toLowerCase().includes((searchTerm || '').toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const showAddOption = searchTerm && !filteredProducts.some(p => (p.productName || '').toLowerCase() === searchTerm.toLowerCase()) && !searchTerm.toLowerCase().includes('other');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
      } else {
        const totalOptions = filteredProducts.length + (allowAdd ? 1 : 0);
        if (activeIndex >= 0 && activeIndex < filteredProducts.length) {
          e.preventDefault();
          handleOptionSelect(filteredProducts[activeIndex]);
          return;
        } else if (allowAdd && activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other');
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      } else if (e.key === 'ArrowLeft' && onPrevField) {
        if (!searchTerm || e.target.selectionStart === 0) {
          e.preventDefault();
          onPrevField();
        }
      }
      return;
    }

    const totalOptions = filteredProducts.length + (allowAdd ? 1 : 0);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
      if (onSelect) setTimeout(() => onSelect(searchTerm), 0);
    } else if (e.key === 'ArrowLeft' && onPrevField) {
      if (!searchTerm || e.target.selectionStart === 0) {
        e.preventDefault();
        setIsOpen(false);
        onPrevField();
      }
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef}>
      <input
        ref={ref}
        className="miracle-input"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder || "Select Product..."}
        style={{ width: '100%', border: 'none', background: 'transparent' }}
      />
      {isOpen && (filteredProducts.length > 0 || showAddOption || true) && (
        <div className="product-select-dropdown" ref={dropdownRef}>
          {filteredProducts.map((p, index) => {
            const hsn = p.productHsnCode || p.hsnCode || 'N/A';
            const stock = p.product_stock || p.productStock || p.stock || 0;
            const rate = (parseFloat(p.product_sale_price || p.productSalePrice || 0)).toFixed(2);
            const isSelected = index === activeIndex;

            return (
              <div
                key={p.id || p._id || index}
                className="product-select-option"
                style={{ backgroundColor: isSelected ? '#1e3a8a' : 'inherit' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleOptionSelect(p);
                }}
                tabIndex="-1"
              >
                <div className="product-option-name" style={{ color: isSelected ? '#fff' : '#1e293b' }}>{p.productName}</div>
                <div className="product-option-details" style={{ color: isSelected ? '#cbd5e1' : '#64748b' }}>
                  {hsn} <span className="product-divider" style={{ color: isSelected ? '#cbd5e1' : '#cbd5e1' }}>||</span>
                  <span className={stock > 0 ? "product-stock-green" : "product-stock-red"}>
                    AVL : {stock}
                  </span>
                  <span className="product-divider" style={{ color: isSelected ? '#cbd5e1' : '#cbd5e1' }}>||</span>
                  <span style={{ color: isSelected ? '#fff' : '#000', fontWeight: 'bold' }}>₹{parseFloat(rate).toFixed(2)}</span>
                </div>
              </div>
            );
          })}
          {allowAdd && (
          <div
            className="product-select-add-option"
            style={{ backgroundColor: activeIndex === filteredProducts.length ? '#f1f5f9' : '#fff', color: '#2563eb' }}
            onMouseDown={(e) => {
              e.preventDefault();
              handleOptionSelect('Other');
            }}
            tabIndex="-1"
          >
            Other (Add New Details)
          </div>
          )}
        </div>
      )}
    </div>
  );
});

const CustomerCreatableSelect = React.forwardRef(({ value, onChange, customers, onAddOption, placeholder, onSelect, onTab, name }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (c, fromKeyboard = false) => {
    const val = c === 'Other' ? 'Other' : (c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name);
    onChange(val, c);
    setSearchTerm(val);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(val, c), 0);
  };

  const filteredCustomers = (customers || []).filter(c => {
    const nameStr = (c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name || '').toLowerCase();
    return nameStr.includes((searchTerm || '').toLowerCase());
  });

  const showAddOption = searchTerm && !filteredCustomers.some(c => {
    const nameStr = (c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name || '').toLowerCase();
    return nameStr === searchTerm.toLowerCase();
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
        // Has value and dropdown is closed — let Enter bubble to form to advance focus
      } else {
        const totalOptions = filteredCustomers.length + 1;
        if (activeIndex >= 0 && activeIndex < filteredCustomers.length) {
          // Do NOT call e.preventDefault() — let event bubble to form's handleFormKeyDown
          handleOptionSelect(filteredCustomers[activeIndex], true);
          return;
        } else if (activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other', true);
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // Dropdown open but nothing highlighted — close and let Enter bubble
        setIsOpen(false);
        return;
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredCustomers.length + 1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <input
        name={name}
        ref={ref}
        className="miracle-input"
        style={{ width: '100%', boxSizing: 'border-box' }}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) { e.preventDefault(); setIsOpen(false); onTab(); return; }
          handleKeyDown(e);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder || "Select Customer..."}
      />
      {isOpen && (filteredCustomers.length > 0 || showAddOption || true) && (
        <div className="po-dropdown" ref={dropdownRef} style={{ width: '400px', maxWidth: '90vw' }}>
          {filteredCustomers.map((c, index) => {
            const isSelected = index === activeIndex;
            const cName = c.customerTradeName || c.customer_trade_name || c.customerName || c.customer_name;
            const gst = c.customerGst || c.customer_gst || 'N/A';
            const state = c.customerState || c.customer_state || c.customerCity || c.customer_city || 'N/A';
            
            // Format balance if exists, else default to 0.00 DR
            let balance = parseFloat(c.balance || c.openingBalance || c.opening_balance || 0).toFixed(2);
            let balType = c.balanceType || c.balance_type || 'DR';
            if (c.balance && !c.balanceType) {
              const val = parseFloat(c.balance);
              balance = Math.abs(val).toFixed(2);
              balType = val >= 0 ? 'DR' : 'CR';
            }
            
            return (
              <div
                key={c.id || c._id || index}
                className={`po-option ${isSelected ? 'active' : ''}`}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}
                onMouseDown={(e) => { e.preventDefault(); handleOptionSelect(c, false); }}
              >
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px', marginBottom: '4px' }}>{cName}</div>
                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>GST: {gst}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span>{state}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span style={{ color: '#3b82f6', fontWeight: '500' }}>Bal: {balance} {balType}</span>
                </div>
              </div>
            );
          })}
          <div
            className={`po-option ${activeIndex === filteredCustomers.length ? 'active' : ''}`}
            style={{ backgroundColor: activeIndex === filteredCustomers.length ? '#f1f5f9' : '#fff', color: '#2563eb', textAlign: 'center', cursor: 'pointer', padding: '10px', fontSize: '13px', fontWeight: 'bold' }}
            onMouseDown={(e) => { e.preventDefault(); handleOptionSelect('Other', false); }}
          >
            + Add New Party "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
});

const TransporterCreatableSelect = React.forwardRef(({ value, onChange, transporters, onAddOption, placeholder, onSelect, onTab, name }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && dropdownRef.current) {
      const activeEl = dropdownRef.current.children[activeIndex];
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [activeIndex, isOpen]);

  const handleInputChange = (e) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const handleOptionSelect = (t, fromKeyboard = false) => {
    const val = t === 'Other' ? 'Other' : (t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name);
    onChange(val, t);
    setSearchTerm(val);
    setIsOpen(false);
    setActiveIndex(-1);
    if (onSelect) setTimeout(() => onSelect(val, t), 0);
  };

  const filteredTransporters = (transporters || []).filter(t => {
    const nameStr = (t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name || '').toLowerCase();
    return nameStr.includes((searchTerm || '').toLowerCase());
  });

  const showAddOption = searchTerm && !filteredTransporters.some(t => {
    const nameStr = (t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name || '').toLowerCase();
    return nameStr === searchTerm.toLowerCase();
  });

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const hasValue = (value && value.trim()) || (searchTerm && searchTerm.trim());
      if (!isOpen) {
        if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
          setActiveIndex(0);
          return;
        }
        // Has value and dropdown closed — let Enter bubble to advance focus
      } else {
        const totalOptions = filteredTransporters.length + 1;
        if (activeIndex >= 0 && activeIndex < filteredTransporters.length) {
          // Do NOT call e.preventDefault() — let event bubble to form's handleFormKeyDown
          handleOptionSelect(filteredTransporters[activeIndex], true);
          return;
        } else if (activeIndex === totalOptions - 1) {
          e.preventDefault();
          handleOptionSelect('Other', true);
          return;
        } else if (!hasValue) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        // Dropdown open but nothing highlighted — close and let Enter bubble
        setIsOpen(false);
        return;
      }
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    const totalOptions = filteredTransporters.length + 1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % (totalOptions || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + totalOptions) % (totalOptions || 1));
    } else if (e.key === 'Tab' && onTab) {
      e.preventDefault();
      setIsOpen(false);
      onTab();
    }
  };

  return (
    <div className="custom-select-container" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <input
        name={name}
        ref={ref}
        className="miracle-input"
        style={{ width: '100%', boxSizing: 'border-box' }}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' && onTab) { e.preventDefault(); setIsOpen(false); onTab(); return; }
          handleKeyDown(e);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder || "Select Transport..."}
      />
      {isOpen && (filteredTransporters.length > 0 || showAddOption || true) && (
        <div className="po-dropdown" ref={dropdownRef} style={{ width: '400px', maxWidth: '90vw' }}>
          {filteredTransporters.map((t, index) => {
            const isSelected = index === activeIndex;
            const tName = t.transporterTradeName || t.transporter_trade_name || t.transporterName || t.transporter_name;
            const gst = t.transporterGst || t.transporter_gst || 'N/A';
            const state = t.transporterState || t.transporter_state || t.transporterCity || t.transporter_city || 'N/A';
            
            return (
              <div
                key={t.id || t._id || index}
                className={`po-option ${isSelected ? 'active' : ''}`}
                style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}
                onMouseDown={(e) => { e.preventDefault(); handleOptionSelect(t, false); }}
              >
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px', marginBottom: '4px' }}>{tName}</div>
                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>GST: {gst}</span>
                  <span style={{ color: '#cbd5e1' }}>||</span>
                  <span>{state}</span>
                </div>
              </div>
            );
          })}
          <div
            className={`po-option ${activeIndex === filteredTransporters.length ? 'active' : ''}`}
            style={{ backgroundColor: activeIndex === filteredTransporters.length ? '#f1f5f9' : '#fff', color: '#2563eb', textAlign: 'center', cursor: 'pointer', padding: '10px', fontSize: '13px', fontWeight: 'bold' }}
            onMouseDown={(e) => { e.preventDefault(); handleOptionSelect('Other', false); }}
          >
            + Add New Transport "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
});

function AddSalesChallanForm({ isOpen, onClose, editingRow, isPage, isEdit: isEditProp }) {
  const { hasPermission } = usePermissionContext();
  const isEdit = isEditProp !== undefined ? isEditProp : !!editingRow;
  const user = JSON.parse(sessionStorage.getItem('user'));
  const firstInputRef = useRef(null);
  const invoiceNameRef = useRef(null);
  const lrNoRef = useRef(null);

  const [formData, setFormData] = useState({
    paymentType: 'Credit',
    customer: '',
    invoiceName: 'AWC',
    time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    saleType: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    invoiceNo: '',
    transporter: '',
    lrNo: '',
    lrDate: '',
    vehicleNo: '',
    containerNo: '',
    sealNo: '',
    discountPercent: '0',
    insurance: '0',
    extraChargesName: 'Extra Charges',
    extraChargesAmount: '0',
    partyDeliveryAddress: '',
    paymentTerms: '100% PAYMENT AGAINST SCAN COPY OF DOCUMENTS. ',
    termsConditions: '1. Goods Once Supplied will not be taken back or exchanged.\n2. We are not responsible for any shortage or damage in transit.\n3. Interest at the rate of 18% shall be charged on all amounts unpaid within 15 days from the supply date.',
    remarks: '',
  });

  const [showDeliveryAddress, setShowDeliveryAddress] = useState(false);
  const [items, setItems] = useState([{
    id: Date.now(),
    category: '',
    product: '',
    hsnCode: '',
    grade: 'PREMIUM',
    color: '',
    unit: 'PCS',
    quantity: '1',
    rate: '',
    total: '0.00'
  }]);

  const [customers, setCustomers] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [saleTypes, setSaleTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [invoiceNameList, setInvoiceNameList] = useState([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isTransporterModalOpen, setIsTransporterModalOpen] = useState(false);

  useEffect(() => {
    fetchMasterData();
    if (isEdit && editingRow) {
      // 1. Initial population from the list data
      const initialData = {
        paymentType: editingRow.paymentType || editingRow.payment_type || 'Credit',
        customer: editingRow.customerName || editingRow.customer_name || '',
        invoiceName: editingRow.invoiceName || editingRow.invoice_name || editingRow.challan_name || 'AW',
        time: editingRow.time || editingRow.prerartion_time || new Date().toLocaleTimeString('en-GB'),
        saleType: editingRow.saleTypeName || editingRow.sale_type_name || '',
        invoiceDate: editingRow.invoiceDate || editingRow.invoice_date || editingRow.challan_date || '',
        invoiceNo: editingRow.invoiceNo || editingRow.invoice_no || editingRow.challan_no || '',
        transporter: editingRow.transporterName || editingRow.transporter_name || '',
        lrNo: editingRow.lrNo || editingRow.lr_no || '',
        lrDate: editingRow.lrDate || editingRow.lr_date || '',
        vehicleNo: editingRow.vehicleNo || editingRow.vehicle_no || '',
        containerNo: editingRow.containerNo || editingRow.container_no || '',
        sealNo: editingRow.sealNo || editingRow.seal_no || '',
        discountPercent: editingRow.discount || '0',
        insurance: editingRow.insurance || '0',
        extraChargesName: editingRow.extraChargesName || editingRow.extra_charges_name || 'Extra Charges',
        extraChargesAmount: editingRow.extraChargesAmount || editingRow.extra_charges_amount || editingRow.extr_charges_amount || '0',
        paymentTerms: editingRow.paymentTerms || editingRow.payment_terms || '',
        termsConditions: editingRow.termsConditions || editingRow.terms_condition || '',
        remarks: editingRow.remarks || '',
        partyDeliveryAddress: editingRow.customerDeliveryAddress || editingRow.customer_delivery_address || editingRow.partyDeliveryAddress || '',
      };
      setFormData(initialData);
      setShowDeliveryAddress(!!(editingRow.customerDeliveryAddress || editingRow.customer_delivery_address || editingRow.partyDeliveryAddress));

      if (editingRow.items) {
        const parseItems = (raw) => {
          if (Array.isArray(raw)) return raw;
          if (typeof raw === 'string') {
            try { return parseItems(JSON.parse(raw)); } catch (e) { return []; }
          }
          return [];
        };
        const itemsList = parseItems(editingRow.items);
        if (itemsList.length > 0) {
          setItems(itemsList.map(it => ({
            id: it.id || it._id || (Date.now() + Math.random()),
            category: it.category || '',
            product: it.product || it.productName || '',
            hsnCode: it.hsnCode || it.hsn_code || '',
            grade: it.grade || 'PREMIUM',
            color: it.color || '',
            unit: it.unit || 'PCS',
            quantity: it.quantity || it.qty || '1',
            rate: it.rate || '0',
            total: it.total || '0.00',
          })));
        }
      }

      // 2. Fetch full detail to get any missing fields (like Container/Seal/Notes)
      fetchChallanDetail(editingRow.id || editingRow._id);
    }
  }, [isEdit, editingRow]);

  const fetchChallanDetail = async (id) => {
    try {
      const data = await fetchApi(`/sales-challan/${id}`);
      if (!data) return;
      
      setFormData(prev => ({
        ...prev,
        paymentType: data.payment_type || data.paymentType || prev.paymentType,
        customer: data.customer_trade_name || data.customerTradeName || data.customer_name || data.customerName || prev.customer,
        invoiceName: data.challan_name || data.invoice_name || data.invoiceName || prev.invoiceName,
        time: data.prerartion_time || data.time || prev.time,
        saleType: data.sale_type_name || data.saleTypeName || prev.saleType,
        invoiceDate: data.challan_date || data.invoice_date || data.invoiceDate || prev.invoiceDate,
        invoiceNo: data.challan_no || data.invoice_no || data.invoiceNo || prev.invoiceNo,
        transporter: data.transporter_trade_name || data.transporterTradeName || data.transporter_name || data.transporterName || prev.transporter,
        lrNo: data.lr_no || data.lrNo || prev.lrNo,
        lrDate: data.lr_date || data.lrDate || prev.lrDate,
        vehicleNo: data.vehicle_no || data.vehicleNo || prev.vehicleNo,
        containerNo: data.container_no || data.containerNo || prev.containerNo,
        sealNo: data.seal_no || data.sealNo || prev.sealNo,
        discountPercent: data.discount || '0',
        insurance: data.insurance || prev.insurance,
        extraChargesName: data.extra_charges_name || data.extraChargesName || prev.extraChargesName,
        extraChargesAmount: data.extr_charges_amount || data.extra_charges_amount || data.extraChargesAmount || prev.extraChargesAmount,
        paymentTerms: data.payment_terms || data.paymentTerms || prev.paymentTerms,
        termsConditions: data.terms_condition || data.termsConditions || prev.termsConditions,
        remarks: data.remarks || prev.remarks,
        partyDeliveryAddress: data.customer_delivery_address || data.customerDeliveryAddress || data.partyDeliveryAddress || prev.partyDeliveryAddress,
      }));
      setShowDeliveryAddress(!!(data.customer_delivery_address || data.customerDeliveryAddress || data.partyDeliveryAddress));

      if (data.items) {
        const parseItems = (raw) => {
          if (Array.isArray(raw)) return raw;
          if (typeof raw === 'string') {
            try {
              const parsed = JSON.parse(raw);
              return parseItems(parsed); // handle double-encoding
            } catch (e) { return []; }
          }
          return [];
        };
        const itemsList = parseItems(data.items);
        if (itemsList.length > 0) {
          setItems(itemsList.map(it => ({
            id: it.id || it._id || (Date.now() + Math.random()),
            category: it.category || '',
            product: it.product || it.productName || '',
            hsnCode: it.hsnCode || it.hsn_code || '',
            grade: it.grade || 'PREMIUM',
            color: it.color || '',
            unit: it.unit || 'PCS',
            quantity: it.quantity || it.qty || '1',
            rate: it.rate || '0',
            total: it.total || '0.00',
          })));
        }
      }
    } catch (error) {
      console.error("Error fetching challan details:", error);
    }
  };

  useEffect(() => {
    if (isOpen || isPage) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [isOpen, isPage]);

  const fetchNextChallanNo = async (typeName) => {
    if (!typeName || isEdit) return;
    try {
      const data = await fetchApi(`/sales-challan/next-no?typeName=${encodeURIComponent(typeName)}`);
      if (data && data.nextNo) {
        setFormData(prev => ({ ...prev, invoiceNo: data.nextNo }));
      }
    } catch (err) {
      console.error("Error fetching next challan number:", err);
    }
  };

  useEffect(() => {
    if (!isEdit && formData.invoiceName) {
      fetchNextChallanNo(formData.invoiceName);
    }
  }, [formData.invoiceName, isEdit]);

  const fetchMasterData = async () => {
    try {
      const result = await fetchApi('/customer');
      setCustomers(result.customers || result || []);
      const transResult = await fetchApi('/transporter');
      setTransporters(transResult.transporters || transResult || []);
      const catResult = await fetchApi('/category');
      setCategoryList((catResult.categories || catResult || []).map(c => c.categoryName));
      const colResult = await fetchApi('/color');
      setColorList((colResult.colors || colResult || []).map(c => c.colorName));
      const saleResult = await fetchApi('/sale-type');
      setSaleTypes(saleResult.saleTypes || saleResult || []);
      const prodResult = await fetchApi('/product');
      setProducts(prodResult.products || prodResult || []);
      const gradeResult = await fetchApi('/grade');
      setGradeList((gradeResult.grades || gradeResult || []).map(g => g.gradeName));
      const invTitleRes = await fetchApi('/invoice-name');
      const names = invTitleRes.invoiceNames || invTitleRes || [];
      setInvoiceNameList(names);
      // Auto-fetch initial challan no based on default settings invoice name (base name + 'C')
      if (!isEdit) {
        let initialInvoiceName = 'AWC';
        if (names.length > 0) {
          const baseName = names[0].invoiceShortName || names[0].invoice_short_name;
          if (baseName) {
            initialInvoiceName = `${baseName}C`;
          }
        }
        setFormData(prev => ({ ...prev, invoiceName: initialInvoiceName }));
        fetchNextChallanNo(initialInvoiceName);
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    }
  };

  const handleRowChange = (id, field, value, prodData) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const newItem = { ...item, [field]: value };
        if (field === 'product' && prodData) {
          const cat = prodData.category_name || (prodData.Category && (prodData.Category.category_name || prodData.Category.categoryName)) || prodData.categoryName;
          if (cat) newItem.category = cat;
        }
        if (field === 'quantity' || field === 'rate') {
          const qty = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
          const rate = parseFloat(field === 'rate' ? value : item.rate) || 0;
          newItem.total = (qty * rate).toFixed(2);
        }
        return newItem;
      }
      return item;
    }));
  };

  const addRow = () => {
    setItems(prev => [...prev, {
      id: Date.now(),
      category: '',
      product: '',
      hsnCode: '',
      grade: 'PREMIUM',
      color: '',
      unit: 'PCS',
      quantity: '1',
      rate: '',
      total: '0.00'
    }]);
  };

  const removeRow = (id) => {
    if (items.length > 1) setItems(prev => prev.filter(it => it.id !== id));
  };

  const handleFormKeyDown = (e) => {
    const form = e.currentTarget;

    // Table to Footer Jump on Tab
    if (e.key === 'Tab') {
      const isInsideTable = e.target.closest('table.miracle-table');
      if (isInsideTable) {
        const paymentTermsInput = form.querySelector('[name="paymentTerms"]');
        if (paymentTermsInput) {
          e.preventDefault();
          paymentTermsInput.focus();
          return;
        }
      }
    }

    if (e.key === 'Enter') {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.type === 'submit') return;

      const headerSequence = [
        'paymentType', 'customer', 'invoiceName', 'saleType', 'invoiceNo', 
        'transporter', 'lrNo', 'lrDate', 'vehicleNo', 'containerNo', 'sealNo'
      ];

      const currentName = e.target.name;
      const sequenceIndex = headerSequence.indexOf(currentName);

      if (sequenceIndex > -1) {
        e.preventDefault();
        const nextName = headerSequence[sequenceIndex + 1];
        if (nextName) {
          const nextEl = form.querySelector(`[name="${nextName}"]`);
          if (nextEl) {
            nextEl.focus();
            if (nextEl.select) nextEl.select();
            return;
          }
        } else if (currentName === 'sealNo') {
          const firstRowInput = form.querySelector('table.miracle-table tbody tr input, table.miracle-table tbody tr select');
          if (firstRowInput) {
            firstRowInput.focus();
            if (firstRowInput.select) firstRowInput.select();
            return;
          }
        }
      }

      // Table Row Enter Logic (Add Row on Rate)
      if (e.target.closest('table.miracle-table') && e.target.name === 'rate') {
        const rows = form.querySelectorAll('table.miracle-table tbody tr');
        const currentRow = e.target.closest('tr');
        if (currentRow === rows[rows.length - 1]) {
          e.preventDefault();
          addRow();
          setTimeout(() => {
            const lastRow = form.querySelector('table.miracle-table tbody tr:last-child');
            const firstInput = lastRow?.querySelector('input, select');
            if (firstInput) {
              firstInput.focus();
              if (firstInput.select) firstInput.select();
            }
          }, 100);
          return;
        }
      }

      // Default focusable logic for table rows and footer
      const focusable = Array.from(form.querySelectorAll(
        'input:not([readonly]):not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button.miracle-submit-btn'
      )).filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });

      const index = focusable.indexOf(e.target);
      if (index > -1) {
        e.preventDefault();
        const next = focusable[index + 1];
        if (next) {
          next.focus();
          if (next.select) next.select();
        }
      }
    }
  };

  const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;

    try {
      const selectedCustomer = customers.find(c => c.customerTradeName === formData.customer || c.customerName === formData.customer || c.customer_name === formData.customer);
      const selectedTransporter = transporters.find(t => t.transporterTradeName === formData.transporter || t.transporterName === formData.transporter || t.transporter_name === formData.transporter);
      const selectedSaleType = saleTypes.find(s => s.saleTypeName === formData.saleType || s.sale_type_name === formData.saleType);

      // Filter out completely blank rows
      const activeItems = items.filter(row => row.category || row.product || (row.rate && row.rate !== ''));

      if (activeItems.length === 0) {
        toast.error("At least one product item is required.");
        return;
      }

      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i];
        if (!item.category) {
          toast.error(`Category is mandatory for Item ${i + 1}`);
          return;
        }
        if (!item.product) {
          toast.error(`Product is mandatory for Item ${i + 1}`);
          return;
        }
        if (!item.rate) {
          toast.error(`Rate is mandatory for Item ${i + 1}`);
          return;
        }

        // Permission Validations for New Category / Product
        if (item.category && !categoryList.some(c => c.toLowerCase() === item.category.toLowerCase())) {
          if (!hasPermission('Category', 'add')) {
            toast.error(`You do not have permission to add a new Category ("${item.category}"). Please select an existing category.`);
            return;
          }
        }
        if (item.product === 'Other' || (item.product && !products.some(p => (p.productName || '').toLowerCase() === item.product.toLowerCase()))) {
          if (!hasPermission('Product', 'add')) {
            const prodName = item.product === 'Other' ? item.newProduct?.name : item.product;
            toast.error(`You do not have permission to add a new Product ("${prodName}"). Please select an existing product.`);
            return;
          }
        }
      }

      // Re-calculate total amount based on activeItems only
      const subTotal = activeItems.reduce((sum, it) => sum + parseFloat(it.total || 0), 0);
      const discountPercent = parseFloat(formData.discountPercent) || 0;
      const discountAmount = (subTotal * discountPercent) / 100;

      const insuranceAmount = (subTotal * (parseFloat(formData.insurance) || 0)) / 100;
      const extraChargesAmount = parseFloat(formData.extraChargesAmount) || 0;

      const taxableVal = subTotal - discountAmount + insuranceAmount + extraChargesAmount;

      const tax1Rate = parseFloat(selectedSaleType?.sale_type_tax1 || selectedSaleType?.saleTypeTax1) || 0;
      const tax2Rate = parseFloat(selectedSaleType?.sale_type_tax2 || selectedSaleType?.saleTypeTax2) || 0;
      const tax1Amount = (taxableVal * tax1Rate) / 100;
      const tax2Amount = (taxableVal * tax2Rate) / 100;
      const totalAmount = taxableVal + tax1Amount + tax2Amount;


      const payload = {
        ...formData,
        discount: parseFloat(formData.discountPercent) || 0,
        discount_type: '%',
        customer_id: selectedCustomer?.id,
        customer_name: selectedCustomer?.customer_name,
        customer_trade_name: selectedCustomer?.customer_trade_name,
        transporter_id: selectedTransporter?.id,
        transporter_name: selectedTransporter?.transporter_name,
        sale_type_id: selectedSaleType?.id,
        sale_type_name: selectedSaleType?.sale_type_name,
        sale_type_tax1: selectedSaleType?.sale_type_tax1,
        sale_type_tax2: selectedSaleType?.sale_type_tax2,
        items: activeItems,
        total_amount: totalAmount,
        company_id: user.company_id
      };

          try {
            const method = isEdit ? 'PUT' : 'POST';
            const url = isEdit ? `${API_BASE_URL}/sales-challan/${editingRow.id}` : `${API_BASE_URL}/sales-challan`;

            const response = await fetch(url, {
              method,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`
              },
              body: JSON.stringify(payload)
            });

            if (response.ok) {
              toast.success(`Sales Challan ${isEdit ? 'Updated' : 'Created'} Successfully`);
              onClose();
            } else {
              const err = await response.json();
              toast.error(err.message || "Failed to save challan");
            }
          } catch (error) {
            console.error(error);
            toast.error("Server Error");
          }
    } catch (error) {
      console.error(error);
      toast.error("Server Error");
    }
  };

  const selectedSaleTypeInfo = saleTypes.find(s => s.saleTypeName === formData.saleType || s.sale_type_name === formData.saleType);
  const tax1Rate = parseFloat(selectedSaleTypeInfo?.saleTypeTax1 || selectedSaleTypeInfo?.sale_type_tax1) || 0;
  const tax2Rate = parseFloat(selectedSaleTypeInfo?.saleTypeTax2 || selectedSaleTypeInfo?.sale_type_tax2) || 0;
  // isLocalSale: both tax rates equal means CGST/SGST (local). Different or only one rate means IGST (interstate).
  const isLocalSale = tax1Rate > 0 && tax2Rate > 0 && tax1Rate === tax2Rate;

  const subTotalCalc = items.reduce((sum, it) => sum + parseFloat(it.total || 0), 0);
  const totalQtyCalc = items.reduce((sum, it) => sum + parseFloat(it.quantity || 0), 0);
  const discountPercentCalc = parseFloat(formData.discountPercent) || 0;
  const discountAmountCalc = (subTotalCalc * discountPercentCalc) / 100;

  const extraChargesAmountCalc = parseFloat(formData.extraChargesAmount) || 0;
  const insuranceAmountCalc = (subTotalCalc * (parseFloat(formData.insurance) || 0)) / 100;

  const taxableValCalc = subTotalCalc - discountAmountCalc + insuranceAmountCalc + extraChargesAmountCalc;

  const tax1AmountCalc = (taxableValCalc * tax1Rate) / 100;
  const tax2AmountCalc = (taxableValCalc * tax2Rate) / 100;
  const totalAmountCalc = taxableValCalc + tax1AmountCalc + tax2AmountCalc;


  const selectedCustObj = customers.find(c => (c.customerTradeName || c.customerName || c.customer_name) === formData.customer);
  const balanceVal = selectedCustObj ? parseFloat(selectedCustObj.balance || 0) : 0;
  const balanceText = selectedCustObj ? `${Math.abs(balanceVal).toFixed(2)} ${balanceVal >= 0 ? 'DR' : 'CR'}` : '0.00 CR';

  if (!isOpen && !isPage) return null;

  const formContent = (
    <div className={isPage ? "page-card" : "modal"} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
      {!isPage && (
        <div className="miracle-header">
          <h2 className="miracle-title">{isEdit ? 'Update Sales Challan' : 'Add Sales Challan'}</h2>
          <button type="button" className="invoice-close" onClick={onClose}>✕</button>
        </div>
      )}
      {isPage && <div className="page-card__title" style={{ textAlign: 'center', fontWeight: '500', fontSize: '24px', padding: '15px' }}>{isEdit ? 'Update Sales Challan' : 'Add Sales Challan'}</div>}

      <div className={isPage ? "page-card__body" : "miracle-body"}>
        <form onKeyDown={handleFormKeyDown} onSubmit={handleSubmit} style={isPage ? { padding: '10px 20px' } : {}}>

          {/* MIRACLE DESIGN TOP SECTION */}
          <div className="miracle-exact-top">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px 30px' }}>

              {/* Column 1 */}
              <div className="miracle-col-compact">
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Cash/Debit</label>
                  <CreatableSelect
                    ref={firstInputRef}
                    name="paymentType"
                    value={formData.paymentType}
                    options={['Debit', 'Credit', 'Cash Memo']}
                    onChange={(val) => setFormData(p => ({ ...p, paymentType: val }))}
                    placeholder="Payment Type"
                    creatable={false}
                  />
                </div>
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Party A/c.</label>
                  <CustomerCreatableSelect
                    name="customer"
                    value={formData.customer}
                    customers={customers}
                    onChange={(val, selected) => {
                      if (val === 'Other') {
                        setIsCustomerModalOpen(true);
                      } else {
                        setFormData(p => ({ 
                          ...p, 
                          customer: val,
                          partyDeliveryAddress: selected ? (selected.customerAddress || selected.customer_address || '') : p.partyDeliveryAddress
                        }));
                      }
                    }}
                    onAddOption={() => setIsCustomerModalOpen(true)}
                    onSelect={() => {}}
                    placeholder="Select Party..."
                  />
                </div>
                <div className="balance-text" style={{ marginLeft: '95px' }}>Bal. : {balanceText}</div>
              </div>

              {/* Column 2 */}
              <div className="miracle-col-compact">
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Time</label>
                  <input
                    name="time"
                    className="miracle-input"
                    value={formData.time}
                    readOnly
                  />
                </div>
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Sale Type</label>
                  <CreatableSelect
                    name="saleType"
                    value={formData.saleType}
                    onChange={(val) => setFormData(p => ({ ...p, saleType: val }))}
                    options={saleTypes.map(s => s.saleTypeName || s.sale_type_name)}
                    placeholder="Select Sale Type"
                  />
                </div>
              </div>

              {/* Column 3 */}
              <div className="miracle-col-compact">
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Bill Date</label>
                  <div className="date-row-miracle">
                    <AirDatePicker
                      name="invoiceDate"
                      value={formData.invoiceDate}
                      onChange={val => setFormData(p => ({ ...p, invoiceDate: val }))}
                    />
                    <div className="day-label">{formData.invoiceDate ? new Date(formData.invoiceDate).toLocaleDateString('en-US', { weekday: 'long' }) : ''}</div>
                  </div>
                </div>
                <div className="miracle-row">
                  <label style={{ width: '95px' }}>Bill No</label>
                  <input
                    name="invoiceNo"
                    className="miracle-input miracle-input-green"
                    value={formData.invoiceNo}
                    onChange={e => setFormData(p => ({ ...p, invoiceNo: e.target.value }))}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="miracle-divider" style={{ margin: '15px 0' }} />

          {/* Transport Section - Row 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1.5fr) minmax(200px, 1fr) minmax(200px, 1fr)', gap: '40px' }}>
            <div className="miracle-row">
              <label>Transport</label>
              <div style={{ display: 'flex', gap: '5px', flex: 1 }}>
                <TransporterCreatableSelect
                  name="transporter"
                  value={formData.transporter}
                  transporters={transporters}
                  onChange={(val) => {
                    if (val === 'Other') {
                      setIsTransporterModalOpen(true);
                    } else {
                      setFormData(p => ({ ...p, transporter: val }));
                    }
                  }}
                  onAddOption={() => setIsTransporterModalOpen(true)}
                  onSelect={() => lrNoRef.current?.focus()}
                  placeholder="Select Transport"
                />
                <button type="button" className="btn-add-miracle" onClick={() => setIsTransporterModalOpen(true)}>Add New</button>
              </div>
            </div>
            <div className="miracle-row">
              <label>L.R. No.</label>
              <input
                ref={lrNoRef}
                name="lrNo"
                className="miracle-input"
                value={formData.lrNo}
                onChange={e => setFormData(p => ({ ...p, lrNo: e.target.value }))}
              />
            </div>
            <div className="miracle-row">
              <label>L.R.Date</label>
              <AirDatePicker
                name="lrDate"
                value={formData.lrDate}
                onChange={val => setFormData(p => ({ ...p, lrDate: val }))}
              />
            </div>
          </div>

          {/* Transport Section - Row 2 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1.5fr) minmax(200px, 1fr) minmax(200px, 1fr)', gap: '40px', marginTop: '4px' }}>
            <div className="miracle-row">
              <label>Vehical No</label>
              <input
                name="vehicleNo"
                className="miracle-input"
                value={formData.vehicleNo}
                onChange={e => setFormData(p => ({ ...p, vehicleNo: e.target.value.toUpperCase() }))}
              />
            </div>
            <div className="miracle-row">
              <label>Container No</label>
              <input
                name="containerNo"
                className="miracle-input"
                value={formData.containerNo}
                onChange={e => setFormData(p => ({ ...p, containerNo: e.target.value }))}
              />
            </div>
            <div className="miracle-row">
              <label>Seal No</label>
              <input
                name="sealNo"
                className="miracle-input"
                value={formData.sealNo}
                onChange={e => setFormData(p => ({ ...p, sealNo: e.target.value }))}
              />
            </div>
          </div>

          {/* ITEMS TABLE */}
          <div className="miracle-table-wrapper">
            <table className="miracle-table">
              <thead>
                <tr>
                  <th style={{ width: '150px' }}>Category *</th>
                  <th style={{ width: '300px' }}>Product Name *</th>
                  <th style={{ width: '100px' }}>HSN Code</th>
                  <th style={{ width: '120px' }}>Grade</th>
                  <th style={{ width: '120px' }}>Color</th>
                  <th style={{ width: '80px' }}>Unit</th>
                  <th style={{ width: '80px' }}>Qty</th>
                  <th style={{ width: '100px' }}>Rate *</th>
                  <th style={{ width: '120px' }}>Amount</th>
                  <th style={{ width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => (
                  <tr key={row.id}>
                    <td>
                      <CreatableSelect
                        value={row.category}
                        onChange={val => handleRowChange(row.id, 'category', val)}
                        options={categoryList} creatable={hasPermission('Category', 'add')}
                        validateOnEnter={true}
                      />
                    </td>
                    <td>
                      <ProductCreatableSelect
                        value={row.product}
                        products={products}
                        category={row.category} allowAdd={hasPermission('Product', 'add')}
                        onChange={(name, p) => {
                          handleRowChange(row.id, 'product', name);
                          if (p) {
                            handleRowChange(row.id, 'product_id', p.id);
                            handleRowChange(row.id, 'hsnCode', p.productHsnCode || '');
                            handleRowChange(row.id, 'rate', p.product_sale_price || p.productSalePrice || '');
                          }
                        }}
                      />
                    </td>
                    <td><input className="miracle-input" value={row.hsnCode} onChange={e => handleRowChange(row.id, 'hsnCode', e.target.value)} /></td>
                    <td>
                      <select className="miracle-input" value={row.grade} onChange={e => handleRowChange(row.id, 'grade', e.target.value)}>
                        {gradeList.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </td>
                    <td>
                      <CreatableSelect
                        value={row.color}
                        onChange={val => handleRowChange(row.id, 'color', val)}
                        options={colorList}
                      />
                    </td>
                    <td><input className="miracle-input" value={row.unit} onChange={e => handleRowChange(row.id, 'unit', e.target.value)} /></td>
                    <td><input className="miracle-input text-right" type="number" value={row.quantity} onChange={e => handleRowChange(row.id, 'quantity', e.target.value)} /></td>
                    <td><input className="miracle-input text-right" type="number" name="rate" value={row.rate} onChange={e => handleRowChange(row.id, 'rate', e.target.value)} /></td>
                    <td className="text-right">{row.total}</td>
                    <td><button type="button" style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer' }} onClick={() => removeRow(row.id)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="miracle-tfoot" style={{ borderTop: '2px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                <tr>
                  <td colSpan="6" style={{ textAlign: 'left', padding: '8px 10px', fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Total Entry: {items.length}</td>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{totalQtyCalc.toFixed(3)}</td>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Sub Total</td>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{subTotalCalc.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <button type="button" className="btn-add-miracle" style={{ marginTop: '10px' }} onClick={addRow}>+ Add Row</button>

          {/* BOTTOM SECTION */}
          <div className="miracle-bottom-section">
            <div className="miracle-notes">
              <div>
                <label>Note / Payment Terms</label>
                <textarea
                  className="miracle-textarea"
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={e => setFormData(p => ({ ...p, paymentTerms: e.target.value }))}
                />
              </div>
              <div>
                <label>Narration / Terms & Conditions</label>
                <textarea
                  className="miracle-textarea"
                  name="termsConditions"
                  style={{ height: '80px' }}
                  value={formData.termsConditions}
                  onChange={e => setFormData(p => ({ ...p, termsConditions: e.target.value }))}
                />
              </div>
              <div style={{ marginTop: '10px' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', cursor: 'pointer', color: '#1e293b' }}>
                  <input type="checkbox" checked={showDeliveryAddress} onChange={e => setShowDeliveryAddress(e.target.checked)} />
                  Change Delivery Address
                </label>
                {showDeliveryAddress && (
                  <input className="miracle-input" style={{ width: '100%', marginTop: '5px', height: '30px' }} name="partyDeliveryAddress" value={formData.partyDeliveryAddress} onChange={e => setFormData(p => ({ ...p, partyDeliveryAddress: e.target.value }))} placeholder="Enter Delivery Address" />
                )}
              </div>
            </div>

            <div className="miracle-summary">
              <div className="summary-row">
                <span>Discount (%)</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                   <input className="miracle-input" name="discountPercent" style={{ width: '60px' }} type="number" value={formData.discountPercent} onChange={e => setFormData(p => ({ ...p, discountPercent: e.target.value }))} />
                   <span>%</span>
                </div>
                <span style={{ color: '#ef4444' }}>-{discountAmountCalc.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Insurance (%)</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                   <input className="miracle-input" name="insurance" style={{ width: '60px' }} type="number" value={formData.insurance} onChange={e => setFormData(p => ({ ...p, insurance: e.target.value }))} />
                   <span>%</span>
                </div>
                <span>{insuranceAmountCalc.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Extra Charges</span>
                <input className="miracle-input" name="extraChargesAmount" style={{ width: '120px' }} type="number" value={formData.extraChargesAmount} onChange={e => setFormData(p => ({ ...p, extraChargesAmount: e.target.value }))} />
                <span>{extraChargesAmountCalc.toFixed(2)}</span>
              </div>
              <div className="miracle-divider" />
              <div className="summary-row bold">
                <span>Taxable Amount</span>
                <span>{taxableValCalc.toFixed(2)}</span>
              </div>
              {isLocalSale ? (
                <>
                  <div className="summary-row">
                    <span>CGST ({tax1Rate}%)</span>
                    <span>{tax1AmountCalc.toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>SGST ({tax2Rate}%)</span>
                    <span>{tax2AmountCalc.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="summary-row">
                  <span>IGST ({(tax1Rate + tax2Rate).toFixed(2)}%)</span>
                  <span>{(tax1AmountCalc + tax2AmountCalc).toFixed(2)}</span>
                </div>
              )}
              <div className="summary-row grand-total">
                <span>Bill Amount</span>
                <span>{totalAmountCalc.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="miracle-actions">
            <button type="submit" className="miracle-submit-btn">OK / Save Invoice</button>
            <button type="button" className="miracle-cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <>
      {isPage ? formContent : <div className="modal-overlay">{formContent}</div>}
      
      <AddCustomerModal 
        isOpen={isCustomerModalOpen} 
        onClose={() => setIsCustomerModalOpen(false)} 
        onSuccess={(newCust) => {
          setCustomers(prev => [...prev, newCust]);
          setFormData(prev => ({ 
            ...prev, 
            customer: newCust.customerTradeName || newCust.customer_trade_name || newCust.customerName,
            partyDeliveryAddress: newCust.customerAddress || newCust.customer_address || ''
          }));
          setIsCustomerModalOpen(false);
        }}
      />

      <AddTransporterModal 
        isOpen={isTransporterModalOpen} 
        onClose={() => setIsTransporterModalOpen(false)} 
        onTransporterAdded={(newTrans) => {
          setTransporters(prev => [...prev, newTrans]);
          setFormData(prev => ({ ...prev, transporter: newTrans.transporterTradeName || newTrans.transporter_trade_name || newTrans.transporterName }));
          setIsTransporterModalOpen(false);
          setTimeout(() => lrNoRef.current?.focus(), 150);
        }}
        onSuccess={(newTrans) => {
          setTransporters(prev => [...prev, newTrans]);
          setFormData(prev => ({ ...prev, transporter: newTrans.transporterTradeName || newTrans.transporter_trade_name || newTrans.transporterName }));
          setIsTransporterModalOpen(false);
          setTimeout(() => lrNoRef.current?.focus(), 150);
        }}
      />
    </>
  );
};

export default AddSalesChallanForm;
